// client/src/api/leads.js
const API_BASE_URL = '/api';

/**
 * Leads API service functions
 */

// Create request headers (authentication handled by cookies)
const getHeaders = () => {
    return {
        'Content-Type': 'application/json'
    };
};

/**
 * Fetch all leads for the authenticated user
 */
export const fetchLeads = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch leads');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching leads:', error);
        throw error;
    }
};

/**
 * Fetch leads grouped by pipeline stage
 */
export const fetchPipeline = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads/pipeline`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch pipeline data');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching pipeline:', error);
        throw error;
    }
};

/**
 * Fetch pipeline statistics
 */
export const fetchPipelineStats = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads/stats`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch pipeline stats');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching pipeline stats:', error);
        throw error;
    }
};

/**
 * Fetch a specific lead by ID
 */
export const fetchLead = async (leadId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch lead');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching lead:', error);
        throw error;
    }
};

/**
 * Create a new lead
 */
export const createLead = async (leadData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(leadData),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create lead');
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating lead:', error);
        throw error;
    }
};

/**
 * Update an existing lead
 */
export const updateLead = async (leadId, updateData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(updateData),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update lead');
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating lead:', error);
        throw error;
    }
};

/**
 * Update lead stage (for drag & drop in pipeline)
 */
export const updateLeadStage = async (leadId, newStage) => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads/${leadId}/stage`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ stage: newStage }),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update lead stage');
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating lead stage:', error);
        throw error;
    }
};

/**
 * Convert lead to client
 */
export const convertLeadToClient = async (leadId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads/${leadId}/convert-to-client`, {
            method: 'POST',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to convert lead to client');
        }

        return await response.json();
    } catch (error) {
        console.error('Error converting lead to client:', error);
        throw error;
    }
};

/**
 * Delete a lead
 */
export const deleteLead = async (leadId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/leads/${leadId}`, {
            method: 'DELETE',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete lead');
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting lead:', error);
        throw error;
    }
};